/**
 * Testeur de daltonisme intégré — Vincent est daltonien (deutéranopie).
 *
 * Simule les 3 principaux types de daltonisme via des matrices de couleurs
 * SVG appliquées à une aire de prévisualisation. Permet à Eric (et QA) de
 * vérifier visuellement toute nouvelle page conçue AVANT déploiement, sans
 * dépendre de Chrome DevTools ni d'extensions tierces.
 *
 * Matrices de conversion RGB → couleur perçue (source : Machado et al. 2009,
 * modèles physiologiquement validés) :
 *   - Deutéranopie (5-6 % hommes) : sensibilité verte absente
 *   - Protanopie   (1-2 % hommes) : sensibilité rouge absente
 *   - Tritanopie   (<1 %)         : sensibilité bleue absente
 */
import { useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const FILTERS = {
  none: null,
  deutéranopie: "0.625 0.375 0    0 0   0.7   0.3   0     0 0   0     0.3   0.7 0 0   0 0 0 1 0",
  protanopie: "0.567 0.433 0     0 0   0.558 0.442 0     0 0   0     0.242 0.758 0 0   0 0 0 1 0",
  tritanopie: "0.95  0.05  0     0 0   0     0.433 0.567 0 0   0     0.475 0.525 0 0   0 0 0 1 0",
};

// Échantillons de couleurs utilisées dans l'app — permet de comparer d'un
// coup d'œil comment la palette apparaît à chaque type de daltonisme.
const SWATCHES = [
  { name: "Bleu (succès)", cls: "bg-blue-600", text: "OK ✓" },
  { name: "Teal (info)", cls: "bg-teal-500", text: "Info" },
  { name: "Ambre (moyen)", cls: "bg-amber-500", text: "Moyen ⏱" },
  { name: "Orange (alerte)", cls: "bg-orange-500", text: "Attention ⚠" },
  { name: "Rouge (erreur)", cls: "bg-red-600", text: "Erreur ✕" },
  { name: "Zinc (neutre)", cls: "bg-zinc-500", text: "Neutre" },
];

export default function ColorblindTester() {
  const [mode, setMode] = useState("none");
  const filterId = mode !== "none" ? `cb-${mode}` : null;
  const matrixValues = FILTERS[mode];

  return (
    <section
      className="bg-white border border-zinc-200 rounded-lg p-5 mb-5"
      data-testid="colorblind-tester"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <Eye size={18} weight="bold" />
            Testeur de daltonisme
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Vincent est daltonien (deutéranopie). Utilise ce testeur pour valider qu&apos;une nouvelle
            page reste lisible pour lui avant déploiement. Sélectionne un mode ci-dessous : la zone
            &laquo; Aperçu palette &raquo; se re-rend à travers le filtre choisi.
          </p>
        </div>
        <div className="flex gap-1 flex-wrap">
          {Object.keys(FILTERS).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setMode(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded border capitalize ${
                mode === k
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
              }`}
              data-testid={`cb-mode-${k}`}
            >
              {k === "none" ? (
                <span className="inline-flex items-center gap-1"><EyeSlash size={12} /> Normal</span>
              ) : k}
            </button>
          ))}
        </div>
      </div>

      {/* SVG defs : filtres feColorMatrix (positionnés hors flux visuel) */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          {Object.entries(FILTERS).filter(([k]) => k !== "none").map(([k, v]) => (
            <filter key={k} id={`cb-${k}`} colorInterpolationFilters="linearRGB">
              <feColorMatrix type="matrix" values={v} />
            </filter>
          ))}
        </defs>
      </svg>

      <div
        className="rounded border border-zinc-200 p-4 bg-zinc-50"
        style={filterId ? { filter: `url(#${filterId})` } : {}}
        data-testid="cb-preview-zone"
      >
        <div className="text-[10px] uppercase text-zinc-500 font-bold mb-2">
          Aperçu palette {mode !== "none" && `— vue ${mode}`}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {SWATCHES.map((sw) => (
            <div
              key={sw.name}
              className={`${sw.cls} text-white rounded p-2 text-center shadow-sm`}
              data-testid={`cb-swatch-${sw.name}`}
            >
              <div className="text-[10px] font-mono opacity-80">{sw.name}</div>
              <div className="text-sm font-bold mt-0.5">{sw.text}</div>
            </div>
          ))}
        </div>
        {/* Exemples badges rythme quiz (colorblind-safe) + rouge isolé (destructif) */}
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-blue-100 text-blue-900 border-blue-300">
            ✓ 45 s/q (confortable)
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-amber-100 text-amber-900 border-amber-300">
            ⏱ 15 s/q (moyen)
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-orange-100 text-orange-900 border-orange-300">
            ⚠ 6 s/q (rushed)
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-red-100 text-red-900 border-red-300">
            ✕ Erreur destructive (rouge isolé — OK pour Vincent)
          </span>
        </div>
      </div>
    </section>
  );
}
